import * as THREE from 'three';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { Ori } from '../../assets/theme/js/variables';

function getViewportSize(mount) {
    return {
        width: Math.max(mount?.clientWidth || 1, 1),
        height: Math.max(mount?.clientHeight || 1, 1),
    };
}

function addStudioLights(scene) {
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.65);
    scene.add(ambientLight);

    const hemiLight = new THREE.HemisphereLight(0xffffff, 0xd6e2ff, 1.05);
    scene.add(hemiLight);

    const keyLight = new THREE.DirectionalLight(0xffffff, 0.95);
    keyLight.position.set(1.8, 1.3, 2.4);
    scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(0xffffff, 0.4);
    fillLight.position.set(-1.6, 0.8, 1.2);
    scene.add(fillLight);
}

function buildMesh(geometry) {
    const material = new THREE.MeshPhysicalMaterial({
        color: 0xdcdcdc,
        side: THREE.DoubleSide,
        roughness: 0.62,
        metalness: 0,
        clearcoat: 0.03,
        clearcoatRoughness: 0.75,
        envMapIntensity: 0.12,
    });

    const mesh = new THREE.Mesh(geometry, material);

    mesh.castShadow = false;
    mesh.receiveShadow = false;

    return mesh;
}

function buildRenderable(geometry) {
    const positionAttribute = geometry.getAttribute('position');

    if (!positionAttribute || positionAttribute.count === 0) {
        return null;
    }

    geometry.rotateX(-Math.PI / 2);
    geometry.computeBoundingSphere();

    const center = geometry.boundingSphere?.center || new THREE.Vector3();
    geometry.translate(-center.x, -center.y, -center.z);
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();

    if (!geometry.getAttribute('normal')) {
        geometry.computeVertexNormals();
    }

    return buildMesh(geometry);
}

function fitCameraToRenderable(camera, controls, renderable) {
    if (!camera || !controls || !renderable) return;

    const targetCamera = camera;
    const targetControls = controls;
    const sphere = new THREE.Box3().setFromObject(renderable).getBoundingSphere(new THREE.Sphere());
    const center = sphere.center.clone();
    const radius = Math.max(sphere.radius, 0.01);
    const fitHeightDistance = radius / Math.tan(THREE.MathUtils.degToRad(targetCamera.fov * 0.5));
    const fitWidthDistance = fitHeightDistance / targetCamera.aspect;
    const distance = Math.max(fitHeightDistance, fitWidthDistance) * 1.18;
    const verticalOffset = Math.min(radius * 0.18, distance * 0.12);

    targetCamera.position.set(center.x, center.y + verticalOffset, center.z + distance);
    targetCamera.near = Math.max(distance / 100, 0.01);
    targetCamera.far = Math.max(distance * 25, 100);
    targetCamera.lookAt(center);
    targetCamera.updateProjectionMatrix();

    targetControls.target.copy(center);
    targetControls.minDistance = Math.max(distance * 0.15, 0.05);
    targetControls.maxDistance = distance * 8;
    targetControls.enablePan = true;
    targetControls.update();
}

function disposeMaterial(material) {
    if (Array.isArray(material)) {
        material.forEach(disposeMaterial);
        return;
    }

    material.dispose();
}

function clearModel(stage, currentRenderableRef) {
    const renderableRef = currentRenderableRef;

    if (!renderableRef.current) {
        return;
    }

    if (stage) {
        stage.remove(renderableRef.current);
    }

    renderableRef.current.traverse((child) => {
        if (child.geometry) {
            child.geometry.dispose();
        }

        if (child.material) {
            disposeMaterial(child.material);
        }
    });

    renderableRef.current = null;
}

function StlViewer({ url, arrayBuffer }) {
    const mountRef = useRef(null);
    const sceneRef = useRef(null);
    const rendererRef = useRef(null);
    const cameraRef = useRef(null);
    const controlsRef = useRef(null);
    const stageRef = useRef(null);
    const pmremGeneratorRef = useRef(null);
    const roomEnvironmentRef = useRef(null);
    const sceneEnvironmentRef = useRef(null);
    const resizeObserverRef = useRef(null);
    const frameIdRef = useRef(null);
    const loadVersionRef = useRef(0);
    const currentRenderableRef = useRef(null);
    // const [fetching, setFetching] = useState(false);

    useEffect(() => {
        const mount = mountRef.current;

        if (!mount) {
            return undefined;
        }

        const { width, height } = getViewportSize(mount);

        sceneRef.current = new THREE.Scene();
        sceneRef.current.background = new THREE.Color(Ori.darkerGray);

        rendererRef.current = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
            powerPreference: 'high-performance',
        });
        rendererRef.current.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        rendererRef.current.setSize(width, height);
        rendererRef.current.outputColorSpace = THREE.SRGBColorSpace;
        rendererRef.current.toneMapping = THREE.ACESFilmicToneMapping;
        rendererRef.current.toneMappingExposure = 1.2;
        // rendererRef.current.setClearColor(0xffffff, 0);

        mount.appendChild(rendererRef.current.domElement);

        cameraRef.current = new THREE.PerspectiveCamera(1, width / height, 0.01, 5000);
        cameraRef.current.position.set(0, 0, 1);

        const headLight = new THREE.DirectionalLight(0xffffff, 0.45);
        headLight.position.set(0, 0, 1);

        // Aim straight ahead
        headLight.target.position.set(0, 0, -1);

        cameraRef.current.add(headLight);
        cameraRef.current.add(headLight.target);

        // Add camera to the scene
        sceneRef.current.add(cameraRef.current);

        controlsRef.current = new OrbitControls(cameraRef.current, rendererRef.current.domElement);
        controlsRef.current.enableDamping = true;
        controlsRef.current.dampingFactor = 0.08;
        controlsRef.current.rotateSpeed = 0.9;
        controlsRef.current.zoomSpeed = 1;
        controlsRef.current.panSpeed = 0.8;
        controlsRef.current.screenSpacePanning = true;
        controlsRef.current.target.set(0, 0, 0);

        pmremGeneratorRef.current = new THREE.PMREMGenerator(rendererRef.current);
        roomEnvironmentRef.current = new RoomEnvironment(rendererRef.current);
        sceneEnvironmentRef.current = pmremGeneratorRef.current.fromScene(roomEnvironmentRef.current, 0.04);
        sceneRef.current.environment = sceneEnvironmentRef.current.texture;

        stageRef.current = new THREE.Group();
        sceneRef.current.add(stageRef.current);

        addStudioLights(sceneRef.current);

        const handleResize = () => {
            if (!rendererRef.current || !cameraRef.current || !mountRef.current) return;

            const viewport = getViewportSize(mountRef.current);

            rendererRef.current.setSize(viewport.width, viewport.height);
            cameraRef.current.aspect = viewport.width / viewport.height;
            cameraRef.current.updateProjectionMatrix();
        };

        if (window.ResizeObserver) {
            resizeObserverRef.current = new window.ResizeObserver(handleResize);
            resizeObserverRef.current.observe(mount);
        } else {
            window.addEventListener('resize', handleResize);
        }

        const animate = () => {
            if (controlsRef.current) {
                controlsRef.current.update();
            }

            if (rendererRef.current && sceneRef.current && cameraRef.current) {
                rendererRef.current.render(sceneRef.current, cameraRef.current);
            }

            frameIdRef.current = window.requestAnimationFrame(animate);
        };

        frameIdRef.current = window.requestAnimationFrame(animate);

        return () => {
            loadVersionRef.current += 1;

            if (frameIdRef.current) {
                window.cancelAnimationFrame(frameIdRef.current);
                frameIdRef.current = null;
            }

            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
                resizeObserverRef.current = null;
            } else {
                window.removeEventListener('resize', handleResize);
            }

            if (controlsRef.current) {
                controlsRef.current.dispose();
                controlsRef.current = null;
            }

            clearModel(stageRef.current, currentRenderableRef);

            if (sceneEnvironmentRef.current) {
                sceneEnvironmentRef.current.dispose();
                sceneEnvironmentRef.current = null;
            }

            if (roomEnvironmentRef.current) {
                roomEnvironmentRef.current.dispose();
                roomEnvironmentRef.current = null;
            }

            if (pmremGeneratorRef.current) {
                pmremGeneratorRef.current.dispose();
                pmremGeneratorRef.current = null;
            }

            if (rendererRef.current) {
                rendererRef.current.dispose();

                if (mountRef.current && rendererRef.current.domElement.parentNode === mountRef.current) {
                    mountRef.current.removeChild(rendererRef.current.domElement);
                }

                rendererRef.current = null;
            }

            sceneRef.current = null;
            cameraRef.current = null;
            stageRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (!stageRef.current) {
            return;
        }

        clearModel(stageRef.current, currentRenderableRef);

        if (!url && !arrayBuffer) {
            return;
        }

        const loader = new STLLoader();
        const loadVersion = loadVersionRef.current + 1;
        loadVersionRef.current = loadVersion;

        if (arrayBuffer) {
            try {
                const geometry = loader.parse(arrayBuffer);

                if (loadVersion !== loadVersionRef.current) {
                    geometry.dispose();
                    return;
                }

                const renderable = buildRenderable(geometry);

                if (!renderable) {
                    geometry.dispose();
                    return;
                }

                currentRenderableRef.current = renderable;
                stageRef.current.add(renderable);
                fitCameraToRenderable(cameraRef.current, controlsRef.current, renderable);
            } catch (error) {
                console.error('Failed to parse STL model.', error);
            }

            return;
        }

        loader.load(
            url,
            (geometry) => {
                if (loadVersion !== loadVersionRef.current) {
                    geometry.dispose();
                    return;
                }

                const renderable = buildRenderable(geometry);

                if (!renderable) {
                    geometry.dispose();
                    return;
                }

                currentRenderableRef.current = renderable;
                stageRef.current.add(renderable);
                fitCameraToRenderable(cameraRef.current, controlsRef.current, renderable);
            },
            undefined,
            (error) => {
                console.error('Failed to load STL model.', error);
            },
        );
    }, [arrayBuffer, url]);

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
                overflow: 'hidden',
            }}
            ref={mountRef}
        />
    );
}

StlViewer.propTypes = {
    url: PropTypes.string,
    arrayBuffer: PropTypes.instanceOf(ArrayBuffer),
};

StlViewer.defaultProps = {
    url: null,
    arrayBuffer: null,
};

export default StlViewer;
